import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FiSend,
  FiLoader,
  FiCheck,
  FiSearch,
  FiFacebook,
  FiInstagram,
  FiUsers,
  FiHome,
  FiUserCheck,
  FiMapPin,
  FiGlobe,
  FiTruck,
  FiBriefcase,
} from "@/lib/icons";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { cn } from "@/lib/utils";
import type { ContactFormData, ServiceInterest, ReferralSource } from "../_types/contact.types";
import { serviceLabels, referralLabels } from "../_types/contact.types";
import type { ReactNode } from "react";

const serviceOptions = Object.entries(serviceLabels) as [ServiceInterest, string][];

const serviceIcons: Record<ServiceInterest, ReactNode> = {
  "residential-investment": <FiHome className="size-4" />,
  "vehicle-asset": <FiTruck className="size-4" />,
  "business-loans": <FiBriefcase className="size-4" />,
};

const referralIcons: Record<ReferralSource, ReactNode> = {
  google: <FiSearch className="size-3.5" />,
  facebook: <FiFacebook className="size-3.5" />,
  instagram: <FiInstagram className="size-3.5" />,
  "friend-family": <FiUsers className="size-3.5" />,
  "real-estate-agent": <FiHome className="size-3.5" />,
  "existing-client": <FiUserCheck className="size-3.5" />,
  signage: <FiMapPin className="size-3.5" />,
  other: <FiGlobe className="size-3.5" />,
};

const referralOptions = Object.entries(referralLabels) as [ReferralSource, string][];

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { executeRecaptcha } = useRecaptcha();
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    services: [],
    loanAmount: "",
    message: "",
    referralSource: undefined,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleService = (service: ServiceInterest) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  const selectReferral = (source: ReferralSource) => {
    setFormData((prev) => ({
      ...prev,
      referralSource: prev.referralSource === source ? undefined : source,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim()) {
      toast.error("Please enter your first name");
      return;
    }
    if (!formData.lastName.trim()) {
      toast.error("Please enter your last name");
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (formData.services.length === 0) {
      toast.error("Please select at least one service");
      return;
    }
    if (!formData.message.trim()) {
      toast.error("Please enter your message");
      return;
    }

    setIsSubmitting(true);

    try {
      const recaptchaToken = await executeRecaptcha("contact_form");

      // Map to API-compatible format
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        services: formData.services,
        loanAmount: formData.loanAmount,
        message: formData.message,
        referralSource: formData.referralSource,
        recaptchaToken,
      };

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json() as { error?: string };

      if (!response.ok) {
        throw new Error(result.error || "Failed to send message");
      }

      toast.success("Message sent! We'll be in touch within 24-48 business hours.");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        services: [],
        loanAmount: "",
        message: "",
        referralSource: undefined,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to send message");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* First Name & Last Name */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium mb-2">
            First Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="firstName"
            name="firstName"
            type="text"
            placeholder="John"
            value={formData.firstName}
            onChange={handleChange}
            className="h-12 rounded-xl"
            required
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium mb-2">
            Last Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="lastName"
            name="lastName"
            type="text"
            placeholder="Smith"
            value={formData.lastName}
            onChange={handleChange}
            className="h-12 rounded-xl"
            required
          />
        </div>
      </div>

      {/* Email & Phone */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email <span className="text-destructive">*</span>
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={handleChange}
            className="h-12 rounded-xl"
            required
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium mb-2">
            Phone Number
          </label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="0400 000 000"
            value={formData.phone}
            onChange={handleChange}
            className="h-12 rounded-xl"
          />
        </div>
      </div>

      {/* Services Interest */}
      <div>
        <label className="block text-sm font-medium mb-3">
          What services are you interested in? <span className="text-destructive">*</span>
        </label>
        <div className="flex flex-wrap gap-3">
          {serviceOptions.map(([value, label]) => {
            const selected = formData.services.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleService(value)}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all cursor-pointer",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:border-primary/40"
                )}
              >
                {selected ? <FiCheck className="size-4" /> : serviceIcons[value]}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Approximate Loan Amount */}
      <div>
        <label htmlFor="loanAmount" className="block text-sm font-medium mb-2">
          Approximate Loan Amount
        </label>
        <Input
          id="loanAmount"
          name="loanAmount"
          type="text"
          placeholder="e.g. $500,000"
          value={formData.loanAmount}
          onChange={handleChange}
          className="h-12 rounded-xl"
        />
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium mb-2">
          Your Message <span className="text-destructive">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          placeholder="Tell us about your situation and goals..."
          value={formData.message}
          onChange={handleChange}
          className="flex w-full rounded-xl border border-input bg-background px-3 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y min-h-[120px]"
          required
        />
      </div>

      {/* How did you hear about us */}
      <div>
        <label className="block text-sm font-medium mb-1">
          How did you hear about us?
        </label>
        <p className="text-xs text-muted-foreground mb-3">Optional</p>
        <div className="flex flex-wrap gap-2">
          {referralOptions.map(([value, label]) => {
            const selected = formData.referralSource === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => selectReferral(value)}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted text-muted-foreground border-transparent hover:text-foreground"
                )}
              >
                {referralIcons[value]}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Submit */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-pill h-12 text-base"
      >
        {isSubmitting ? (
          <>
            <FiLoader className="mr-2 size-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <FiSend className="mr-2 size-4" />
            Send Message
          </>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        By submitting this form, you agree to our{" "}
        <a href="/privacy-policy" className="underline hover:text-foreground">
          Privacy Policy
        </a>
        . We'll respond within 24-48 business hours.
      </p>
    </form>
  );
}
